import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer';
import axios from 'axios';
import * as pdfjs from 'pdfjs-dist/webpack';
import { useCallback, useEffect, useState } from 'react';
import { SetState } from 'src/types/generic';
import { validSignedUrl } from 'src/utils/strings';
import font from '../../assets/fonts/Roboto-Bold.ttf';
import { styles } from './PDFStyles';

export type PrintPDFProps = {
  receiptId: string;
  buyerDetails: Record<string, any>;
  supplierDetails: Record<string, any>;
  receiptLineItems: any[];
  po1: string[];
  po2: string[];
  notes: any;
  history: any[];
  showHistory: boolean;
  showNotes: boolean;
  showAttachment: boolean;
  attachDocuments: string[];
  isPoHistoryLoading: boolean;
  setIsPrintLoading: SetState<boolean>;
};

export const isImage = (url: string) =>
  url?.toLowerCase()?.includes('.png') || url?.toLowerCase()?.includes('.jpg');

export const PrintPDF = ({
  receiptId,
  buyerDetails,
  supplierDetails,
  receiptLineItems,
  po1,
  po2,
  notes,
  history,
  showNotes,
  showHistory,
  showAttachment,
  attachDocuments,
  setIsPrintLoading,
}: PrintPDFProps) => {
  const [resolveAttachmentDocs, setResolveAttachmentDocs] = useState<string[]>([]);
  Font.register({
    family: 'Roboto',
    src: font,
    fonts: [
      { src: font },
      {
        src: font,
        fontWeight: 700,
      },
    ],
  });

  const lineItemColumns = [
    {
      title: 'PO Qty',
      key: 'poQty',
    },
    {
      title: 'Cur. Doc Qty',
      key: 'currentDocQty',
    },
    {
      title: 'Prev. Doc Qty',
      key: 'previousDocQty',
    },
    {
      title: 'Variance Qty',
      key: 'varianceQty',
    },
    {
      title: 'Item #',
      key: 'item',
    },
    {
      title: 'Desc',
      key: 'itemDescription',
    },
    {
      title: 'PO GL',
      key: 'poglCode',
    },
    {
      title: 'UOM',
      key: 'uom',
    },
    {
      title: 'Item Note',
      key: 'itemNoteValue',
    },
  ];
  const historyColumns = [
    { title: 'Action', key: 'action' },
    { title: 'Date', key: 'actionDateFormatted' },
    { title: 'Document', key: 'document' },
  ];
  const POSectionColumns1 = ['PO#', 'Status', 'Document Date'];
  const POSectionColumns2 = [
    'Delivery Date and Time',
    'Delivery Driver Name',
    'Delivery Company Name',
  ];

  const getUrl = useCallback(async (src: string) => {
    const response = await axios.get(src, { responseType: 'blob' });
    const blob: Blob = response.data;
    return window.URL.createObjectURL(blob);
  }, []);

  const renderPage = useCallback(async (data: string) => {
    const imagesList: string[] = [];
    const canvas = document.createElement('canvas');
    canvas.setAttribute('class', 'canv');
    const pdf = await pdfjs.getDocument({ data }).promise; //pics
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1.5 });
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const renderContext = {
        canvasContext: canvas.getContext('2d'),
        viewport: viewport,
      };
      await page.render(renderContext).promise;
      const img = canvas.toDataURL('image/png');
      imagesList.push(img);
    }
    return imagesList;
  }, []);

  const UrlUploader = useCallback(
    async (url: string): Promise<string[]> => {
      try {
        const blobUrl = await getUrl(url);
        const response = await fetch(blobUrl);
        const blob = await response.blob();

        return await new Promise<string[]>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = async (e: ProgressEvent<FileReader>) => {
            try {
              const data: string = atob(e.target!.result!.toString().replace(/.*base64,/, ''));
              const imageList = await renderPage(data);
              resolve(imageList);
            } catch (error) {
              reject(error);
            }
          };
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        // Handle any errors here
        console.error(error);
        throw error; // Rethrow the error to propagate it
      }
    },
    [getUrl, renderPage]
  );

  useEffect(() => {
    if (showAttachment && attachDocuments.length > 0) {
      setIsPrintLoading?.(true);
      Promise.all(
        attachDocuments.map((url) => {
          if (!isImage(url)) {
            return UrlUploader(`${validSignedUrl(url)}`);
          } else {
            return getUrl(`${validSignedUrl(url)}`);
          }
        })
      )
        .then((res) => {
          const flattenedArray = res.reduce((acc, curr) => {
            if (typeof curr === 'string') {
              return [...acc, curr];
            }
            return [...acc, ...curr];
          }, []);
          setResolveAttachmentDocs(
            typeof flattenedArray === 'string' ? ([] as string[]) : flattenedArray
          );
        })
        .catch((err) => console.error(err))
        .finally(() => setTimeout(() => setIsPrintLoading(false), 1000));
    }
  }, [UrlUploader, attachDocuments, getUrl, setIsPrintLoading, showAttachment]);

  return (
    <Document>
      <Page size="A4" style={styles.body}>
        <Text style={styles.title}>Receipt# {receiptId}</Text>
        <View style={styles.pageFlow}>
          <View style={{ ...styles.POSection, ...styles.section }}>
            <View style={styles.POSectionColumn}>
              {POSectionColumns1.map((columnTitle: string, index: number) => (
                <View style={styles.poSectionItem} key={columnTitle}>
                  <Text style={styles.poSectionHeading}>{columnTitle}</Text>
                  <Text style={styles.poSectionValue}>{po1[index]}</Text>
                </View>
              ))}
            </View>
            <View style={styles.POSectionColumn}>
              {POSectionColumns2.map((columnTitle: string, index) => (
                <View style={styles.poSectionItem} key={columnTitle}>
                  <Text style={styles.poSectionHeading}>{columnTitle}</Text>
                  <Text style={styles.poSectionValue}>{po2[index]}</Text>
                </View>
              ))}
            </View>
          </View>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Contact Information</Text>
            <View style={styles.divider} />

            <View style={styles.contactInfo}>
              <View style={styles.contactInfoSection}>
                <Text style={styles.subTitle}>Buyer Details:</Text>
                <Text style={styles.description}>{buyerDetails?.name}</Text>
                <Text style={styles.description}>{buyerDetails?.companyName}</Text>
                <Text style={styles.description}>{buyerDetails?.addressLine1}</Text>
                <Text style={styles.description}>{buyerDetails?.addressLine3}</Text>
                <Text style={styles.description}>{buyerDetails?.addressLine2}</Text>
                <Text style={styles.description}>{buyerDetails?.city}</Text>
                <Text style={styles.description}>{buyerDetails?.state}</Text>
                <Text style={styles.description}>{buyerDetails?.country}</Text>
                <Text style={styles.description}>{buyerDetails?.email}</Text>
                <Text style={styles.description}>{buyerDetails?.zipcode}</Text>
              </View>
              <View style={styles.contactInfoSection}>
                <Text style={styles.subTitle}>Supplier Details:</Text>
                <Text style={styles.description}>{supplierDetails?.name}</Text>
                <Text style={styles.description}>{supplierDetails?.companyName}</Text>
                <Text style={styles.description}>{supplierDetails?.addressLine1}</Text>
                <Text style={styles.description}>{supplierDetails?.addressLine2}</Text>
                <Text style={styles.description}>{supplierDetails?.addressLine3}</Text>
                <Text style={styles.description}>{supplierDetails?.city}</Text>
                <Text style={styles.description}>{supplierDetails?.state}</Text>
                <Text style={styles.description}>{supplierDetails?.country}</Text>
                <Text style={styles.description}>{supplierDetails?.email}</Text>
                <Text style={styles.description}>{supplierDetails?.zipcode}</Text>
              </View>
            </View>
          </View>

          {showNotes && (
            <View style={{ ...styles.section }}>
              <Text style={{ ...styles.sectionTitle }}>Notes</Text>
              <View style={styles.divider} />
              <Text style={styles.description}>{notes}</Text>
            </View>
          )}
          <View>
            <Text style={styles.sectionTitle}>Line Items</Text>
            <View style={styles.divider} />
          </View>

          <View style={styles.table}>
            <View style={styles.tableHeader}>
              {lineItemColumns.map((columnTitle) => (
                <Text
                  style={{
                    ...styles.tableHeaderCell,
                    ...styles[columnTitle.key as keyof typeof styles],
                  }}
                  key={columnTitle.key}
                >
                  {columnTitle.title}
                </Text>
              ))}
            </View>
            <View style={styles.tableBody}>
              {receiptLineItems.map((val: any, rowIndex: number) => (
                <View key={rowIndex} style={styles.tableRow}>
                  {lineItemColumns.map((column, index: number) => (
                    <Text
                      key={index}
                      style={{ ...styles.tableCell, ...styles[column.key as keyof typeof styles] }}
                    >
                      {val[column.key]}
                    </Text>
                  ))}
                </View>
              ))}
            </View>
          </View>

          {showHistory && (
            <View>
              <View>
                <Text style={styles.sectionTitle}>Receipt History</Text>
                <View style={styles.divider} />
              </View>

              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  {historyColumns.map((columnTitle) => (
                    <Text
                      style={{
                        ...styles.tableHeaderCell,
                        ...styles[columnTitle.key as keyof typeof styles],
                      }}
                      key={columnTitle.key}
                    >
                      {columnTitle.title}
                    </Text>
                  ))}
                </View>
                <View style={styles.tableBody}>
                  {history?.map((historyItem: any, rowIndex: number) => (
                    <View key={rowIndex} style={styles.tableRow}>
                      {historyColumns?.map((column) => (
                        <Text
                          key={column.key}
                          style={{
                            ...styles.tableCell,
                            ...styles[column.key as keyof typeof styles],
                          }}
                        >
                          {historyItem?.[column.key]}
                        </Text>
                      ))}
                    </View>
                  ))}
                </View>
              </View>
            </View>
          )}
          {showAttachment && (
            <View>
              <View>
                <Text style={styles.sectionTitle}>Attached Documents</Text>
                <View style={styles.divider} />
                <View>
                  {resolveAttachmentDocs.map((url, indx) =>
                    url.includes('data:') ? (
                      <Image key={indx} src={url} style={{ height: '700px' }} />
                    ) : (
                      <Image key={indx} src={url} />
                    )
                  )}
                </View>
              </View>
            </View>
          )}
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

export default PrintPDF;
